import { Tag, Typography, type TableColumnsType } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import { useTranslation } from 'react-i18next';
import type { CreateOrganizationRelationshipResponseDto } from '@evimo/client/models/CreateOrganizationRelationshipResponseDto';
import type { GroupedRelationship } from '~/app/hooks/organisationRelationship/useOrganizationRelationships';

const { Text } = Typography;

type Params = {
  orgMap: Map<string, string>;
  onEdit: (rel: CreateOrganizationRelationshipResponseDto) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
};

type RelationshipTableColumns = {
  parentColumns: TableColumnsType<GroupedRelationship>;
  childColumns: TableColumnsType<CreateOrganizationRelationshipResponseDto>;
  flatColumns: TableColumnsType<CreateOrganizationRelationshipResponseDto>;
};

export function useRelationshipTableColumns({
  orgMap,
  onEdit,
  onDelete,
  isDeleting,
}: Params): RelationshipTableColumns {
  const { t } = useTranslation('organizationRelationship');

  const parentColumns: TableColumnsType<GroupedRelationship> = [
    {
      title: t('relationship.serviceProvider', 'Organization'),
      dataIndex: 'orgName',
      key: 'orgName',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: t('relationship.clientCount', 'Client Organizations'),
      key: 'count',
      render: (_: unknown, record: GroupedRelationship) => (
        <Text type="secondary">{record.relationships.length} clients</Text>
      ),
    },
  ];

  const childColumns: TableColumnsType<CreateOrganizationRelationshipResponseDto> =
    [
      {
        title: t('relationship.clientOrg', 'Partner Organization'),
        dataIndex: 'partnerOrgId',
        key: 'partnerOrgId',
        render: (partnerOrgId: string) =>
          orgMap.get(partnerOrgId) ?? partnerOrgId,
      },
      {
        title: t('relationship.relationType', 'Relation Type'),
        dataIndex: 'relationType',
        key: 'relationType',
        render: (type: string) => <Tag>{t(`relationType.${type}`, type)}</Tag>,
      },
      {
        title: t('relationship.created', 'Created'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (date: string) =>
          date ? new Date(date).toLocaleDateString() : '-',
      },
      {
        title: t('common.actions', 'Actions'),
        key: 'actions',
        align: 'right',
        render: (
          _: unknown,
          record: CreateOrganizationRelationshipResponseDto,
        ) => (
          <TableRowActionsMenu
            loading={isDeleting}
            items={[
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: t('common.edit', 'Edit'),
                onClick: () => onEdit(record),
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: t('common.delete', 'Delete'),
                danger: true,
                onClick: () => onDelete(record.id),
              },
            ]}
          />
        ),
      },
    ];

  const flatColumns: TableColumnsType<CreateOrganizationRelationshipResponseDto> =
    [
      {
        title: t('relationship.clientOrg', 'Partner Organization'),
        dataIndex: 'partnerOrgId',
        key: 'partnerOrgId',
        render: (partnerOrgId: string) =>
          orgMap.get(partnerOrgId) ?? partnerOrgId,
      },
      {
        title: t('relationship.relationType', 'Relation Type'),
        dataIndex: 'relationType',
        key: 'relationType',
        render: (type: string) => <Tag>{t(`relationType.${type}`, type)}</Tag>,
      },
      {
        title: t('relationship.created', 'Created'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (date: string) =>
          date ? new Date(date).toLocaleDateString() : '-',
      },
      {
        title: t('common.actions', 'Actions'),
        key: 'actions',
        align: 'right',
        render: (
          _: unknown,
          record: CreateOrganizationRelationshipResponseDto,
        ) => (
          <TableRowActionsMenu
            loading={isDeleting}
            items={[
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: t('common.edit', 'Edit'),
                onClick: () => onEdit(record),
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: t('common.delete', 'Delete'),
                danger: true,
                onClick: () => onDelete(record.id),
              },
            ]}
          />
        ),
      },
    ];

  return { parentColumns, childColumns, flatColumns };
}
